import { Loading } from "@components/LoadingIndicator";
import { FundraiserCard } from "@components/NewFundraiserCard";
import { MasonryList } from "@components/layout/MasonryList";
import React, { useCallback, useState } from "react";

import {
  FundraiserResponse,
  NonprofitResponse,
  UserResponse,
} from "@every.org/common/src/codecs/entities";
import { FundraisersFullResponse } from "@every.org/common/src/routes/fundraiser";

import { useLoggedInUserOrUndefined } from "src/context/AuthContext/hooks";
import { dispatchNonprofitsAction } from "src/context/NonprofitsContext";
import { NonprofitActionType } from "src/context/NonprofitsContext/types";
import { useAsyncEffect } from "src/hooks/useAsyncEffect";
import {
  fetchUserFundraisers,
  UserFundraisersFetchStatus,
} from "src/pages/Fundraiser/fetchFundraiser";
import { EmptyStateCard } from "src/pages/Manage/FundraisersPage";

const NUM_FUNDRAISERS_TO_FETCH = 100;
const NUM_FUNDRAISERS_TO_RENDER = 100;

const UserFundraisersList: React.FCC<{
  user: UserResponse;
}> = ({ user }) => {
  const { id } = user;
  const [fundraisers, setFundraisers] = useState<
    FundraisersFullResponse | UserFundraisersFetchStatus
  >(UserFundraisersFetchStatus.FETCHING_USER_FUNDRAISERS);
  const [listOfFundraisers, setListOfFundraisers] = useState<
    FundraiserResponse[] | UserFundraisersFetchStatus.FETCHING_USER_FUNDRAISERS
  >(UserFundraisersFetchStatus.FETCHING_USER_FUNDRAISERS);
  const [itemsToFetch] = useState(NUM_FUNDRAISERS_TO_FETCH);
  const [nonprofitsMap, setNonprofitsMap] = useState<
    Map<NonprofitResponse["id"], NonprofitResponse>
  >(new Map());

  const loggedInUser = useLoggedInUserOrUndefined()?.id;

  const asyncOperation = useCallback(
    () => fetchUserFundraisers(id, itemsToFetch, id != loggedInUser),
    [id, itemsToFetch, loggedInUser]
  );
  const handleResponse = useCallback(
    (fundraisers: FundraisersFullResponse | UserFundraisersFetchStatus) => {
      setFundraisers(fundraisers);
      if (
        fundraisers !== UserFundraisersFetchStatus.USER_FUNDRAISERS_NOT_FOUND &&
        fundraisers !== UserFundraisersFetchStatus.FETCHING_USER_FUNDRAISERS
      ) {
        if (
          fundraisers.fundraisers.length > NUM_FUNDRAISERS_TO_RENDER &&
          itemsToFetch > NUM_FUNDRAISERS_TO_RENDER
        ) {
          setListOfFundraisers(
            fundraisers.fundraisers.slice(0, NUM_FUNDRAISERS_TO_RENDER)
          );
        } else {
          setListOfFundraisers(fundraisers.fundraisers);
        }
        if (fundraisers.nonprofits.length > 0 && nonprofitsMap) {
          const newNonprofits: [NonprofitResponse["id"], NonprofitResponse][] =
            fundraisers.nonprofits.map((nonprofit) => [
              nonprofit.id,
              nonprofit,
            ]);
          setNonprofitsMap(new Map([...nonprofitsMap, ...newNonprofits]));
          dispatchNonprofitsAction({
            type: NonprofitActionType.ADD_NONPROFITS,
            data: fundraisers.nonprofits,
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemsToFetch]
  );
  const handleError = useCallback(
    () => setFundraisers(UserFundraisersFetchStatus.USER_FUNDRAISERS_NOT_FOUND),
    []
  );

  useAsyncEffect({
    asyncOperation,
    handleResponse,
    handleError,
  });

  if (fundraisers === UserFundraisersFetchStatus.FETCHING_USER_FUNDRAISERS) {
    return <Loading />;
  }

  return (
    <React.Fragment>
      {fundraisers !== UserFundraisersFetchStatus.USER_FUNDRAISERS_NOT_FOUND &&
      fundraisers?.fundraisers.length > 0 ? (
        listOfFundraisers !==
          UserFundraisersFetchStatus.FETCHING_USER_FUNDRAISERS && (
          <MasonryList
            items={listOfFundraisers
              .filter((fundraiser) => nonprofitsMap.get(fundraiser.nonprofitId))
              .map((fundraiser) => {
                const nonprofit = nonprofitsMap.get(
                  fundraiser.nonprofitId
                ) as NonprofitResponse;
                return {
                  key: fundraiser.id,
                  elem: (
                    <FundraiserCard
                      fundraiser={fundraiser}
                      nonprofit={nonprofit}
                    />
                  ),
                };
              })}
          />
        )
      ) : (
        <EmptyStateCard />
      )}
    </React.Fragment>
  );
};

export default UserFundraisersList;
